import { Alert } from "@msuite/ui/alert";
import { useTranslation } from "@msuite/ui/localization";
import { moment } from "@msuite/utils/date";
import { useMemo } from "react";
import { useAttendancesHistory } from "@/hooks/use-attendances-history";

/** Props Interface */
interface AttendanceAttentionProps {
	userId: string;
}

/**
 * What the last 30 days are waiting on the user to fix. It renders nothing when there is nothing
 * to do, so the screen carries no permanent slot for it.
 */
export function AttendanceAttention({ userId }: AttendanceAttentionProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { history } = useAttendancesHistory(userId);

	/** Computed */
	// An open row on *today* is the person being checked in, which is the ordinary state and not
	// something to report. Only a day already behind us can hold a check-out nobody made.
	const attention = useMemo(() => {
		const today = moment().db();
		let missingCheckOutDays = 0;
		let rejectedEntries = 0;

		for (const day of history) {
			rejectedEntries += day.rejectedEntries;
			if (day.openEntries > 0 && moment(day.date).db() < today) missingCheckOutDays += 1;
		}

		return { missingCheckOutDays, rejectedEntries };
	}, [history]);

	/** Guard */
	if (!attention.missingCheckOutDays && !attention.rejectedEntries) return null;

	/** Render */
	return (
		<div className="v-stack">
			{attention.missingCheckOutDays > 0 && (
				<Alert
					variant="warning"
					title={t("users.missing_check_out_title")}
					description={
						attention.missingCheckOutDays === 1
							? t("users.missing_check_out_description_one")
							: t("users.missing_check_out_description_count", {
									count: String(attention.missingCheckOutDays),
								})
					}
				/>
			)}
			{attention.rejectedEntries > 0 && (
				<Alert
					variant="danger"
					title={t("users.rejected_attendances_title")}
					description={
						attention.rejectedEntries === 1
							? t("users.rejected_attendances_description_one")
							: t("users.rejected_attendances_description_count", {
									count: String(attention.rejectedEntries),
								})
					}
				/>
			)}
		</div>
	);
}
